"use client"

import { Home, LogIn, LogOut, Lightbulb, User } from "lucide-react"
import { usePathname } from "next/navigation"
import React, { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"

import NavLink from "@packages/web/components/NavLink"
import Spinner from "@packages/web/components/Spinner"
import SignLink from "@packages/web/components/SignLink"
import useGetAuthentication from "@packages/base/hooks/use-get-authentication"

const menuItemClass =
  "border-none block w-full text-left px-4 py-3 hover:bg-muted transition-colors text-foreground disabled:opacity-70 !font-normal !bg-transparent cursor-pointer hover:!scale-100"
const currentPageItemClass =
  "block w-full text-left px-4 py-3 cursor-default text-primary font-semibold bg-muted [&_svg]:text-primary-base"

type MenuItem = {
  to: string
  label: string
  Icon: React.ComponentType<{ className?: string }>
}

const primaryNavItems: MenuItem[] = [
  { to: "/dashboard/think", label: "Think", Icon: Lightbulb },
  { to: "/dashboard/explorer", label: "Explorer", Icon: Home },
]

const profileNavItem: MenuItem = {
  to: "/dashboard/profile",
  label: "Profile",
  Icon: User,
}

function MenuNavItem({
  closeMenu,
  isCurrent,
  item,
}: {
  closeMenu: () => void
  isCurrent: boolean
  item: MenuItem
}) {
  const { Icon, label, to } = item
  const content = (iconClass: string) => (
    <div className="flex items-center gap-2.5 text-sm">
      <Icon className={`w-4 h-4 shrink-0 ${iconClass}`} />
      {label}
    </div>
  )

  if (isCurrent) {
    return (
      <div aria-current="page" className={currentPageItemClass}>
        {content("text-primary-base")}
      </div>
    )
  }

  return (
    <NavLink className={menuItemClass} size="lg" to={to} variant="secondary" onClick={closeMenu}>
      {content("text-muted-foreground")}
    </NavLink>
  )
}

export default function NavMenu() {
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { data: authentication } = useGetAuthentication(
    { redirectIfNotAuthenticated: false },
    queryClient,
  )
  const isCurrentPage = (to: string) => pathname === to || pathname.startsWith(`${to}/`)

  const closeMenu = () => setIsMenuOpen(false)
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  return (
    <div className={`relative flex items-center gap-1 ${isMenuOpen ? "z-1000" : ""}`}>
      <button
        onClick={toggleMenu}
        className="cursor-pointer flex flex-col justify-center items-center w-8 h-8 space-y-1.5 focus:outline-none text-foreground/70 hover:text-foreground transition-colors"
        aria-label="Menu"
      >
        <span
          className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "rotate-45 translate-y-[6px] w-[28px]" : ""}`}
        ></span>
        <span
          className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""}`}
        ></span>
        <span
          className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-[7px] w-[28px]" : ""}`}
        ></span>
      </button>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 min-h-[max(100dvh,100lvh)] w-full overscroll-none [-webkit-transform:translateZ(0)]"
            onClick={closeMenu}
          />
          <nav className="absolute right-0 top-full mt-3 w-52 bg-card rounded-xl shadow-lg z-50 overflow-hidden py-1">
            {primaryNavItems.map((item) => (
              <MenuNavItem
                key={item.to}
                closeMenu={closeMenu}
                isCurrent={isCurrentPage(item.to)}
                item={item}
              />
            ))}
            {authentication?.isAuthenticated ? (
              <MenuNavItem
                closeMenu={closeMenu}
                isCurrent={isCurrentPage(profileNavItem.to)}
                item={profileNavItem}
              />
            ) : null}
            <div className="my-1 border-t border-border/50 mx-2 mt-4" role="separator" />
            <SignLink
              className={menuItemClass}
              onSignoutSuccess={closeMenu}
              signinTo="/auth?sign=signin&redirect=/dashboard/think"
              render={({ isPending, isAuthenticated }) => (
                <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  {isAuthenticated ? (
                    <>
                      {isPending ? (
                        <Spinner
                          height={1}
                          width={1}
                          className="shrink-0 [&>div]:border-foreground"
                        />
                      ) : (
                        <LogOut className="w-4 h-4 text-muted-foreground" />
                      )}
                      {isPending ? "Déconnexion..." : "Se déconnecter"}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 text-muted-foreground" />
                      Se connecter
                    </>
                  )}
                </div>
              )}
            />
          </nav>
        </>
      )}
    </div>
  )
}
